import * as React from 'react';
import classNames from 'classnames';
import IconCheckmark from '../../icons/icon_checkmark';
import IconX from '../../icons/icon_x';
import isVeneerIcon from '../../utils/is_veneer_icon';
import { OptionInterface } from './types';
import { IconInterface } from '../../icons';
import useStyles from './use_styles';
import highContrastMediaQuery from '../../utils/high_contrast_media_query';
import { useTheme } from '@veneer/theme';
import Tooltip from '../../tooltip';
import isElementOverflowing from '../../utils/is_element_overflowing';

const Option = React.forwardRef<
  HTMLLIElement,
  OptionInterface &
    Omit<React.HTMLAttributes<HTMLLIElement>, keyof OptionInterface>
>(
  (
    {
      children,
      disabled = false,
      icon,
      multiple = false,
      selected = false,
      wrap = true,
      ...props
    },
    ref
  ) => {
    const styles = useStyles();
    const theme = useTheme();
    const isPill = theme.shape === 'pill';
    const spanRef = React.useRef<HTMLDivElement>(null);
    const [openTooltip, setOpenTooltip] = React.useState(false);

    const hasIcon = React.isValidElement<IconInterface>(icon);

    const customIcon =
      hasIcon &&
      React.cloneElement(icon, {
        className: 'vn-option__icon',
        disabled,
        customStyle: styles.customIcon,
      });

    const multiSelectIcon = multiple && selected && (
      <>
        <IconCheckmark
          customStyle={[styles.multiSelectIcon]}
          className="vn-option__iconbutton icon_checkmark"
        />
        <IconX
          customStyle={[styles.multiSelectIcon]}
          className="vn-option__iconbutton icon_x"
        />
      </>
    );

    const isHc = theme.mode === 'contrast';

    const handleMouseEnter = React.useCallback(() => {
      if (isElementOverflowing(spanRef?.current)) {
        setOpenTooltip(true);
      }
    }, []);

    const handleOptionMouseLeave = React.useCallback(
      () => setOpenTooltip(false),
      []
    );

    return (
      <li
        css={[
          styles.root.base,
          hasIcon && styles.root.withIcon,
          isPill && [
            styles.root.pill.base,
            hasIcon && styles.root.pill.withIcon,
          ],
          multiple && styles.root.multiple,
          isHc && styles.root.hc,
          highContrastMediaQuery(theme),
        ]}
        aria-disabled={disabled}
        aria-selected={selected}
        className={classNames('vn-option', {
          disabled,
          multiple,
          icon,
          'vn-icon': isVeneerIcon(icon),
        })}
        ref={ref}
        role="option"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleOptionMouseLeave}
        {...props}
      >
        {(customIcon || multiSelectIcon) && (
          <div css={styles.icons}>
            {multiSelectIcon}
            {customIcon}
          </div>
        )}

        <Tooltip
          content={children}
          forTruncation
          useJsPositioning
          open={openTooltip}
          placement="trailing-end"
          portal
        >
          <div css={styles.label} className="vn-option__label">
            <span
              ref={spanRef}
              css={[styles.text.base, wrap && styles.text.wrap]}
            >
              {children}
            </span>
          </div>
        </Tooltip>
      </li>
    );
  }
);

Option.displayName = 'Option';

export default Option;
